export * from './qraft/index';

import { requestFn } from '@openapi-qraft/react';
import type {
  OperationSchema,
  RequestFnInfo,
  RequestFnOptions,
  RequestFnResponse,
} from '@openapi-qraft/tanstack-query-react-types';
import { QueryClient } from '@tanstack/react-query';
import { apiLanguage } from '~/i18n';
import { createAPIClient } from './qraft/index';

/**
 * Exported because the language switcher has to invalidate it. Query keys are
 * built from the operation and its parameters, and the language is neither — it
 * rides in a header — so nothing about a language change makes a cached response
 * look stale. Without an explicit invalidation, switching to Spanish leaves every
 * already-fetched screen showing the English the server sent the first time, and
 * only screens visited *afterwards* look translated. See `useInterfaceLanguage`.
 */
export const queryClient = new QueryClient();

// Get API endpoint from environment variable or use default
export const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT || '/api';

/**
 * Every v3 request goes through here, which is why the language header belongs
 * here and not at any call site: the server answers filter labels, option lists
 * and preference headings in the request's language, and a call that forgets the
 * header silently gets the account's stored preference instead of the one the
 * footer switcher is currently showing.
 *
 * Read per request rather than captured once — `i18n.changeLanguage` can land
 * between any two calls.
 */
function customRequestFn<TData, TError>(
  schema: OperationSchema,
  requestInfo: RequestFnInfo,
  options?: RequestFnOptions,
): Promise<RequestFnResponse<TData, TError>> {
  const newRequestInfo = {
    ...requestInfo,
    credentials: 'include',
    headers: {
      ...requestInfo.headers,
      'x-wbs-language': apiLanguage(),
    },
  };
  return requestFn(schema, newRequestInfo as RequestFnInfo, options);
}

const api_client = createAPIClient({
  baseUrl: API_ENDPOINT,
  requestFn: customRequestFn,
  queryClient,
});

export default api_client.v3;
